import { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { SliderField } from '../SliderField/SliderField';
import { VideoPreview } from '../VideoPreview/VideoPreview';
import type { VideoFitType, VideoSourceType, VideoProvider } from '@builder4/shared/types/video';
import {
  parseVideoUrl,
  getVideoThumbnailUrl,
  validateEmbedCode,
  extractIframeSrc,
} from '@builder4/shared/utils/videoHelpers';

interface BackgroundVideoProps {
  className?: string;
  sourceType?: VideoSourceType;
  onSourceTypeChange: (sourceType: VideoSourceType) => void;
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  provider?: VideoProvider;
  onVideoUrlChange: (
    url: string | null,
    provider?: VideoProvider,
    thumbnailUrl?: string | null,
  ) => void;
  embedCode?: string | null;
  onEmbedCodeChange: (embedCode: string | null) => void;
  onThumbnailChange?: (thumbnailUrl: string | null) => void;
  mode?: VideoFitType;
  onModeChange: (mode: VideoFitType) => void;
  opacity?: number;
  onOpacityChange: (opacity: number) => void;
}

export const BackgroundVideo: React.FC<BackgroundVideoProps> = ({
  className = '',
  sourceType = 'url',
  onSourceTypeChange,
  videoUrl,
  thumbnailUrl,
  provider = 'youtube',
  onVideoUrlChange,
  embedCode,
  onEmbedCodeChange,
  onThumbnailChange,
  mode = 'fill',
  onModeChange,
  opacity = 100,
  onOpacityChange,
}) => {
  const [urlInput, setUrlInput] = useState(videoUrl || '');
  const [embedInput, setEmbedInput] = useState(embedCode || '');
  const [embedError, setEmbedError] = useState<string | null>(null);

  // Toggle state for source type selector
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const sourceTypeRefs = useRef<Record<VideoSourceType, HTMLButtonElement | null>>({
    url: null,
    embed: null,
  });

  // Update toggle highlight position when source type changes
  useLayoutEffect(() => {
    const activeEl = sourceTypeRefs.current[sourceType];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [sourceType]);

  // Sync URL input with props
  useEffect(() => {
    setUrlInput(videoUrl || '');
  }, [videoUrl]);

  // Sync embed input with props
  useEffect(() => {
    setEmbedInput(embedCode || '');
  }, [embedCode]);

  // Auto-fetch thumbnail when embed code changes (URL mode fetches in handleUrlSubmit)
  // Only runs when embedCode changes, not when sourceType changes
  useEffect(() => {
    if (!onThumbnailChange || sourceType !== 'embed') return;

    const videoSource = embedCode ? extractIframeSrc(embedCode) : null;

    if (!videoSource) {
      onThumbnailChange(null);
      return;
    }

    const parsed = parseVideoUrl(videoSource);
    if (!parsed.isValid) {
      return;
    }

    let cancelled = false;

    getVideoThumbnailUrl(parsed.provider, parsed.videoId).then((thumbnail) => {
      if (!cancelled && thumbnail !== thumbnailUrl) {
        onThumbnailChange(thumbnail);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [embedCode, sourceType, thumbnailUrl, onThumbnailChange]);

  const handleSourceTypeChange = (newType: VideoSourceType) => {
    setIsAnimated(true);
    onSourceTypeChange(newType);
    setEmbedError(null);
  };

  const handleUrlSubmit = useCallback(async () => {
    const trimmedUrl = urlInput.trim();

    if (!trimmedUrl) {
      onVideoUrlChange(null, undefined, null);
      return;
    }

    const parsed = parseVideoUrl(trimmedUrl);
    if (!parsed.isValid) {
      onVideoUrlChange(trimmedUrl, undefined, null);
      return;
    }

    const thumbnail = await getVideoThumbnailUrl(parsed.provider, parsed.videoId);
    onVideoUrlChange(trimmedUrl, parsed.provider, thumbnail);
  }, [urlInput, onVideoUrlChange]);

  const handleProviderChange = (value: string) => {
    onVideoUrlChange(videoUrl || null, value as VideoProvider, thumbnailUrl);
  };

  const handleEmbedCodeSubmit = useCallback(() => {
    const trimmedCode = embedInput.trim();

    if (!trimmedCode) {
      setEmbedError(null);
      onEmbedCodeChange(null);
      return;
    }

    const validation = validateEmbedCode(trimmedCode);
    if (!validation.isValid) {
      setEmbedError(validation.error || 'Invalid embed code');
      return;
    }

    setEmbedError(null);
    onEmbedCodeChange(trimmedCode);
  }, [embedInput, onEmbedCodeChange]);

  const handleClearClick = useCallback(() => {
    // Clear all video data for both source types
    setUrlInput('');
    setEmbedInput('');
    setEmbedError(null);
    onVideoUrlChange(null, undefined, null);
    onEmbedCodeChange(null);
    onThumbnailChange?.(null);
  }, [onVideoUrlChange, onEmbedCodeChange, onThumbnailChange]);

  const hasVideo = Boolean(videoUrl) || Boolean(embedCode) || Boolean(thumbnailUrl);

  return (
    <div className={clsx('flex flex-col gap-3', className)}>
      {/* Source Type Toggle */}
      <div className="flex w-full max-w-[216px] items-center justify-between gap-4">
        <p className="text-button-sm font-medium text-neutral-500">Source</p>
        <div className="relative flex h-8 w-[160px] items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
          <div
            className={clsx(
              'absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] text-indigo-500 outline outline-offset-[-1px] outline-[#e9ecf0]',
              isAnimated && 'transition-all duration-300',
            )}
            style={{
              width: `${highlightStyle.width}px`,
              transform: `translateX(${highlightStyle.left}px)`,
            }}
          >
            <p className="text-button-sm font-semibold capitalize">
              {sourceType === 'url' ? 'URL' : 'Embed'}
            </p>
          </div>
          {(['url', 'embed'] as const).map((type) => (
            <button
              key={type}
              ref={(el) => {
                sourceTypeRefs.current[type] = el;
              }}
              onClick={() => handleSourceTypeChange(type)}
              className="text-button-sm relative z-10 flex-1 cursor-pointer px-2 py-1.5 text-neutral-400 capitalize"
              data-testid={`video-source-type-${type}`}
            >
              {type === 'url' ? 'URL' : 'Embed'}
            </button>
          ))}
        </div>
      </div>

      {/* URL Input */}
      {sourceType === 'url' && (
        <Input
          className="max-w-[216px]"
          placeholder="Paste video URL"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onBlur={handleUrlSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleUrlSubmit();
          }}
        />
      )}

      {/* Type Selection (only for URL type) */}
      {sourceType === 'url' && (
        <SelectField
          onValueChange={handleProviderChange}
          className="max-w-[216px]"
          defaultValue={provider}
          size="medium"
          text="Provider"
        >
          <SelectItem value="youtube">YouTube</SelectItem>
          <SelectItem value="vimeo">Vimeo</SelectItem>
          <SelectItem value="loom">Loom</SelectItem>
          <SelectItem value="wistia">Wistia</SelectItem>
          <SelectItem value="vidyard">Vidyard</SelectItem>
          <SelectItem value="brightcove">Brightcove</SelectItem>
          <SelectItem value="cloudflare-stream">Cloudflare Stream</SelectItem>
        </SelectField>
      )}

      {/* Embed Code Input */}
      {sourceType === 'embed' && (
        <>
          <Textarea
            className={clsx('max-w-[216px] resize-none', embedError && 'border-red-500')}
            placeholder="Paste iframe embed code"
            value={embedInput}
            onChange={(e) => setEmbedInput(e.target.value)}
            onBlur={handleEmbedCodeSubmit}
            rows={3}
          />
          {embedError && <span className="text-xs text-red-500">{embedError}</span>}
        </>
      )}

      {/* Video Preview */}
      {hasVideo && <VideoPreview thumbnailUrl={thumbnailUrl} onClearClick={handleClearClick} />}

      {/* Mode Selection */}
      {hasVideo && (
        <SelectField
          className="max-w-[216px]"
          placeholder="Mode"
          size="small"
          text="Mode"
          defaultValue={mode}
          onValueChange={(value) => onModeChange(value as VideoFitType)}
        >
          <SelectItem value="fill">Fill</SelectItem>
          <SelectItem value="fit">Fit</SelectItem>
        </SelectField>
      )}

      {/* Opacity Slider */}
      {hasVideo && (
        <SliderField
          className="w-full max-w-[216px]"
          label="Opacity"
          max={100}
          min={0}
          onChange={onOpacityChange}
          value={opacity}
        />
      )}
    </div>
  );
};
